import { Badge, Container, } from "react-bootstrap";
import {
  ArrowLeftCircle,
  Calendar2Week,
  CarFront,
  CarFrontFill,
  ClockHistory,
  CurrencyDollar,
  FileText,
  InboxFill,
  PeopleFill,
  PersonCircle,
  Receipt,
} from "react-bootstrap-icons";
import { Outlet, useLoaderData, useLocation } from "react-router-dom";
import { useParamIds } from "./Data/Common";
import { ParkerDetails } from "./Data/ApiTypes";
import { PageTitle } from "./Controls/PageTitle";
import { cents, Money, showMoneyShort } from "./Data/Money";
import { equal } from "dinero.js";
import { CollapsibleSidebar } from "./Controls/CollapsibleSidebar";
import { CollapsibleMenuItem } from "./Controls/CollapsibleMenuItem";
import { useEffect, useState } from "react";

function OrdersMenuBadge({ balance }: { balance: Money }) {
  const zero = cents(0);
  const isPaid = equal(balance, zero);
  const [animate, setAnimate] = useState(false);
  const [isFirstRender, setIsFirstRender] = useState(true);


  useEffect(() => {
    // Skip the first render
    if (isFirstRender) {
      setIsFirstRender(false);
      return;
    }

    setAnimate(true);
    const timeout = setTimeout(() => setAnimate(false), 600);
    return () => clearTimeout(timeout);
  }, [balance]);

  return (
    <Badge
      className={`border border-primary bg-white fw-normal text-primary position-relative transition-all ${
        animate ? "balance-shimmer" : ""
      }`}
    >
      {isPaid ? "Paid" : `${showMoneyShort(balance)} Due`}
    </Badge>
  );
}

interface NavState {
  pathname: string;
  state?: any;
}

export function AppFacilityParker() {
  const location = useLocation();
  const { facilityId, parkerId } = useParamIds();
  const parker = useLoaderData() as ParkerDetails;
  const [balance, setBalance] = useState(parker.balance);
  const [navStack, setNavStack] = useState<NavState[]>([]);

  // Scroll to top when location changes
  useEffect(() => {
        // Disable browser's scroll restoration
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    
    // Scroll to top when location changes
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    const currentState: NavState = {
      pathname: location.pathname,
      state: location.state,
    };

    setNavStack((prevStack) => {
      const last = prevStack[prevStack.length - 1];
      if (!last || last.pathname !== currentState.pathname) {
        return [...prevStack, currentState];
      }
      return prevStack;
    });
  }, [location]);

  //for handling mobile and desktop versions of these pages
  const [menuClassName, contentClassName] =
    location.pathname === `/facility/${facilityId}/parker/${parkerId}` ? ["", "d-none"] : ["d-none", ""];

  const active = location.pathname === `/facility/${facilityId}/parker/${parkerId}`;

  const backOptions: Record<string, { url: string; text: string }> = {
    status: {
      url: `/facility/${facilityId}/status`,
      text: "Back to Transient",
    },
    parker: {
      url: `/facility/${facilityId}/parker`,
      text: "Back to Monthly",
    },
    parkerDetail: {
      url: `/facility/${facilityId}/parker/${parkerId}`,
      text: "Back to Parker",
    },
    parkerInvoices: {
      url: `/facility/${facilityId}/parker/${parkerId}/invoice`,
      text: "Back to Invoices",
    },
    payments: {
      url: `/facility/${facilityId}/finance/payment`,
      text: "Back to Payment",
    },
    parkersList: {
      url: `/facility/${facilityId}/parker/${location.state?.parentParkerId}/parkers`,
      text: "Back to Parkers",
    }
  };

  const defaultOption = {
    url: `/facility/${facilityId}/parker`,
    text: "Back to Monthly",
  };

  let from = location.state?.from;
  if ((!from || !backOptions[from]) && navStack.length > 0) {
      from = navStack[0].state?.from;
  }

  let backUrl = backOptions[from]?.url || defaultOption.url;
  let backText = backOptions[from]?.text || defaultOption.text;

  return (
    <Container fluid className="fullheight pt-2">
      <div className="d-flex fullheight">
        <CollapsibleSidebar 
          className={`${menuClassName} d-print-none`}
          header={
            <div className="d-flex align-items-center justify-content-center gap-2">
              <PageTitle className="fw-normal fs-4 text-truncate">{parker.name}</PageTitle>
              <span className="fs-6">
                <OrdersMenuBadge balance={balance} />
              </span>
            </div>
          }
        >
          <CollapsibleMenuItem special default icon={<ArrowLeftCircle />} to={backUrl} text={backText} />
          <CollapsibleMenuItem active={active} default icon={<PersonCircle />} to="./overview" text="Overview" />
          <CollapsibleMenuItem icon={<FileText />} to="./plans" text="Plans" /> 
          <CollapsibleMenuItem icon={<Calendar2Week />} to="./reservations" text="Reservations" />
          <CollapsibleMenuItem icon={<CarFront />} to="./parkers" text="Parkers" />
          <CollapsibleMenuItem icon={<ClockHistory />} to="./balance/history" text="History" />
          <CollapsibleMenuItem icon={<InboxFill />} to="./order" text="Orders" />
          <CollapsibleMenuItem icon={<Receipt />} to="./invoice" text="Invoices" />
          <CollapsibleMenuItem icon={<CurrencyDollar />} to="./payment" text="Payments" />
          <CollapsibleMenuItem icon={<CarFrontFill />} to="./vehicle" text="Vehicles" />
          <CollapsibleMenuItem icon={<PeopleFill />} to="./delegate" text="Users" />
        </CollapsibleSidebar>
        <div
          className={`${contentClassName} d-md-block fullheight scroll-y-auto flex-fill`}
        >
          <Outlet key={`${facilityId}-${parkerId}-${location.pathname}`}
          context={{ balance, setBalance }} />
        </div>
      </div>
    </Container>
  );
}